import { Badge, Col, Form, FormInstance, Select } from 'antd';
import { useWatch } from 'antd/lib/form/Form';
import { isEmpty } from 'lodash';
import { useCallback, useMemo } from 'react';
import Customers from '~/@core/domain/model/customer/Customers';
import ProductCategory from '~/@core/domain/model/product/ProductCategory';
import ProductPreferenceColor from '~/@core/domain/model/product/ProductPreferenceColor';
import ProductQuality from '~/@core/domain/model/product/ProductQuality';
import Site from '~/@core/domain/model/site/Site';
import DefaultRow from '~/components/bosons/DefaultRow';
import { RecipientDetailed } from '~/domain/model';
import ProductGroupWithCategories from '~/domain/model/PriceReport/ProductGroupWithCategories';
import { IProductSimple } from '~/domain/model/PriceReport/ProductSimple';
import { IFilters, IFormFilter } from '~/zustand/salesStatement/useSalesStatementByTypeStore';

const maxTagPlaceholder = (omittedValues: Site[]) => {
  return (
    <Badge>{`+ ${omittedValues.length} selecionado${omittedValues.length > 1 ? 's' : ''}`}</Badge>
  );
};

interface Props {
  filters: IFilters;
  form: FormInstance<IFormFilter>;
}

export default function HiddenIntermediationFilters({ filters, form }: Props) {
  const handleCategoryId = useCallback(() => {
    form.resetFields(['groupIds', 'productIds', 'sizes']);
  }, [form]);

  const handleGroupId = useCallback(() => {
    form.resetFields(['productIds', 'sizes']);
  }, [form]);

  const handleProductId = useCallback(() => {
    form.resetFields(['sizes']);
  }, [form]);

  const formValues = useWatch([], form);

  const mappedGroups = useMemo(() => {
    const categoryIds = formValues?.categoryIds;

    if (isEmpty(categoryIds)) {
      return filters.groups.data;
    }

    return filters.groups.data.filter(group =>
      group.categoryIds.some(categoryId => categoryIds.includes(categoryId)),
    );
  }, [filters, formValues?.categoryIds]);

  const mappedProducts = useMemo(() => {
    const groupIds = formValues?.groupIds;

    if (isEmpty(groupIds)) {
      const mappedGroupIds = mappedGroups.map(group => group.id);
      return filters.products.data.filter(product => mappedGroupIds.includes(product.groupId));
    }

    return filters.products.data.filter(product => groupIds.includes(product.groupId));
  }, [filters, formValues?.groupIds, mappedGroups]);

  const mappedSizes = useMemo(() => {
    const productIds = formValues?.productIds;

    if (isEmpty(productIds)) {
      const mappedProductIds = mappedProducts.map(product => product.id);
      return filters.sizes.data.filter(size =>
        size.productIds.some(productId => mappedProductIds.includes(productId)),
      );
    }

    return filters.sizes.data.filter(size =>
      size.productIds.some(productId => productIds.includes(productId)),
    );
  }, [filters, formValues?.productIds, mappedProducts]);

  return (
    <DefaultRow>
      <Col xs={24} lg={12} xl={8}>
        <Form.Item label="Categoria" name="categoryIds">
          <Select
            placeholder="Selecione uma ou mais categorias"
            showSearch
            showArrow
            allowClear
            loading={filters.categories.isLoading}
            disabled={filters.categories.isLoading}
            optionLabelProp="label"
            optionFilterProp="children"
            onChange={handleCategoryId}
            mode="multiple"
            maxTagCount={1}
            maxTagPlaceholder={maxTagPlaceholder}
          >
            {filters.categories.data.map((item: ProductCategory) => (
              <Select.Option
                key={item.id}
                value={item.id}
                label={item.description}
                title={item.description}
              >
                {item.description}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col xs={24} lg={12} xl={8}>
        <Form.Item label="Grupo" name="groupIds">
          <Select
            placeholder="Selecione um ou mais grupos"
            showSearch
            showArrow
            allowClear
            loading={filters.groups.isLoading}
            disabled={filters.groups.isLoading}
            optionLabelProp="label"
            optionFilterProp="children"
            onChange={handleGroupId}
            mode="multiple"
            maxTagCount={1}
            maxTagPlaceholder={maxTagPlaceholder}
          >
            {mappedGroups.map((item: ProductGroupWithCategories) => (
              <Select.Option
                key={item.id}
                value={item.id}
                label={item.description}
                title={item.description}
              >
                {item.description}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col xs={24} lg={12} xl={8}>
        <Form.Item label="Produto" name="productIds">
          <Select
            placeholder="Selecione um ou mais produtos"
            showSearch
            showArrow
            allowClear
            loading={filters.products.isLoading}
            disabled={filters.products.isLoading}
            optionLabelProp="label"
            optionFilterProp="children"
            onChange={handleProductId}
            mode="multiple"
            maxTagCount={1}
            maxTagPlaceholder={maxTagPlaceholder}
          >
            {mappedProducts.map((item: IProductSimple) => (
              <Select.Option
                key={item.id}
                value={item.id}
                label={item.fullName}
                title={item.fullName}
              >
                {item.fullName}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col xs={24} lg={12} xl={6}>
        <Form.Item label="Tamanho" name="sizes">
          <Select
            placeholder="Selecione um ou mais tamanhos"
            showSearch
            showArrow
            allowClear
            loading={filters.sizes.isLoading}
            disabled={filters.sizes.isLoading}
            optionLabelProp="label"
            optionFilterProp="children"
            mode="multiple"
            maxTagCount={1}
            maxTagPlaceholder={maxTagPlaceholder}
          >
            {mappedSizes.map((item: RecipientDetailed) => (
              <Select.Option
                key={item.description}
                value={item.description}
                label={item.description}
                title={item.description}
              >
                {item.description}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col xs={24} lg={12} xl={6}>
        <Form.Item label="Cor" name="colorIds">
          <Select
            placeholder="Selecione uma ou mais cores"
            showSearch
            showArrow
            allowClear
            loading={filters.colors.isLoading}
            disabled={filters.colors.isLoading}
            optionLabelProp="label"
            optionFilterProp="children"
            mode="multiple"
            maxTagCount={1}
            maxTagPlaceholder={maxTagPlaceholder}
          >
            {filters.colors.data.map((item: ProductPreferenceColor) => (
              <Select.Option
                key={item.id}
                value={item.id}
                label={item.description}
                title={item.description}
              >
                {item.description}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col xs={24} lg={12} xl={6}>
        <Form.Item label="Qualidade" name="quality">
          <Select
            placeholder="Selecione uma qualidade"
            showSearch
            allowClear
            loading={filters.qualities.isLoading}
            disabled={filters.qualities.isLoading}
            optionFilterProp="children"
          >
            {filters.qualities.data.map((item: ProductQuality) => (
              <Select.Option key={item.description} value={item.description}>
                {item.description}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col xs={24} lg={12} xl={6}>
        <Form.Item label="Cliente" name="customerIds">
          <Select
            placeholder="Selecione um ou mais clientes"
            showSearch
            showArrow
            allowClear
            loading={filters.customers.isLoading}
            disabled={filters.customers.isLoading}
            optionLabelProp="label"
            optionFilterProp="children"
            mode="multiple"
            maxTagCount={1}
            maxTagPlaceholder={maxTagPlaceholder}
          >
            {filters.customers.data.map((item: Customers) => (
              <Select.Option
                key={item.id}
                value={item.id}
                label={`${item.accountCode} - ${item.name}`}
                title={`${item.accountCode} - ${item.name}`}
              >
                {`${item.accountCode} - ${item.name}`}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
    </DefaultRow>
  );
}
